import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ModalWindow from '../ModalWindow/ModalWindow';
import ModalButton from '../ModalWindow/ModalButton/ModalButton';
import './SupplyFarmsConfirmModal.css';
import {SUPPLY_COLOR, WAR_COLOR} from '../../../styles/constants';
import {selectShowSupplyFarmsConfirmModal} from '../../../redux/reducers/privateStateReducer/selectors';
import {hideSupplyFarmsConfirmModal} from '../../../redux/actions';
import store from '../../../redux/store';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {handleCompleteTurn} from '../../../utils/gameState/turn/handleCompleteTurn';
import {selectIsParallelSupply} from '../../../redux/reducers/sharedStateReducer/selectors';
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from '../../../utils/gameState/supply/parallelSupplySelectors';

/**
 * Игрок одновременной фазы снабжения сетевой игры, за которого модалка
 * завершает ход, или null — если фаза уже закрылась или игрок закончил свою
 * часть (модалка могла провисеть до конца таймера фазы)
 */
const selectParallelSupplyActorStillActing = (state) => (
  selectIsParallelSupplyPhase(state) ? selectSupplyActingPlayer(state) : null
);

/** Модалка одновременной фазы снабжения, которой больше не за кого действовать */
const selectIsParallelSupplyModalStale = (state) => selectIsParallelSupply(state)
  && !selectParallelSupplyActorStillActing(state);

/**
 * Подтверждение завершения хода, когда у игрока еще остались фермы, способные
 * передать снабжение отряду (см. CompletePhaseButton -> checkIfPlayerHasSupplyCapableFarm).
 * Смонтирована на уровне GameContent, как и прочие модалки (см.
 * ConstructionActionsConfirmModal — иначе рисуется под боковыми панелями).
 */
const SupplyFarmsConfirmModal = () => {
  const dispatch = useDispatch();
  const showModal = useSelector(selectShowSupplyFarmsConfirmModal);
  const isParallelSupplyModalStale = useSelector(selectIsParallelSupplyModalStale);

  // Одновременная фаза снабжения: фаза закрылась или сервер завершил часть
  // игрока по таймеру, пока модалка была открыта, — вопрос уже не актуален.
  // Закрываем флаг, а не только прячем: иначе модалка всплыла бы снова в
  // снабжении следующего раунда
  useEffect(() => {
    if (showModal && isParallelSupplyModalStale) {
      dispatch(hideSupplyFarmsConfirmModal());
    }
  }, [showModal, isParallelSupplyModalStale, dispatch]);

  if (!showModal) {
    return null;
  }

  const handleCancel = () => {
    dispatch(hideSupplyFarmsConfirmModal());
  };

  const handleConfirm = () => {
    dispatch(hideSupplyFarmsConfirmModal());

    const state = store.getState();
    if (selectIsParallelSupply(state)) {
      // Завершаем СВОЮ часть одновременной фазы — перепроверив, что фаза еще
      // идет и игрок ее не закончил
      const supplyActor = selectParallelSupplyActorStillActing(state);
      if (supplyActor) {
        handleCompleteTurn(store, true, true, REGISTRY_SINGLETON.getSoundManager(), {playerId: supplyActor.id});
      }
      return;
    }

    handleCompleteTurn(store, true, true, REGISTRY_SINGLETON.getSoundManager());
  };

  return (
    <ModalWindow handleClose={handleCancel} className={'supply-farms-confirm-modal'}>
      <div className="supply-farms-confirm-modal-content">
        <h2>
          {'У вас еще остались фермы'}
          <img
            className="supply-farms-confirm-modal-farm-img"
            src={'/img/hexes/farm 1_1.3a45c526.png'}
            alt={'Ферма'}
          />
          {', которые могут передать '}
          <span style={{color: SUPPLY_COLOR}}>снабжение</span>
          <img
            className="supply-farms-confirm-modal-supply-img"
            src={'/img/icons/supply.png'}
            alt={'Снабжение'}
          />
          {' отрядам.'}
          <br/>
          {'Уверены, что хотите завершить ход?'}
        </h2>
      </div>

      <div className="supply-farms-confirm-modal-footer">
        <ModalButton
          onClick={handleConfirm}
          text={'Подтвердить'}
          className={'supply-farms-confirm-modal-button'}
          style={{backgroundColor: WAR_COLOR}}
        />
        <ModalButton
          onClick={handleCancel}
          text={'Вернуться'}
          className={'supply-farms-confirm-modal-button'}
        />
      </div>
    </ModalWindow>
  );
};

export default SupplyFarmsConfirmModal;
